import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import type { ServiceDetail, TelemetryEvent } from "@/lib/telemetry";
import { formatTs, type TelemetryTz } from "@/components/telemetry/format";
import { useCatalog } from "@/app/localization";

/**
 * What each marker means, in the words a support engineer needs.
 *
 * Descriptive, never diagnostic: a rejected frame is a frame whose checksum did
 * not validate. Whether that was radio interference, a truncating NAT or a
 * failing unit is not something the archive knows.
 */
// Keyed by the archive's raw marker; DBG/EKM/GPS badges show the marker itself.
const MARKER: Record<string, { value: "dbg" | "ekm" | "gps" | "rejected"; variant: "neutral" | "warning" }> = {
  DBG: { value: "dbg", variant: "neutral" },
  EKM: { value: "ekm", variant: "neutral" },
  GPS: { value: "gps", variant: "neutral" },
  REJECTED: { value: "rejected", variant: "warning" },
};

export function serviceMarkerOf(event: TelemetryEvent): string {
  const detail = event.detail as ServiceDetail | null;
  return (detail?.marker || event.state || "—").toString();
}

/**
 * The frames that are not telemetry, kept apart from the ones that are.
 *
 * They used to sit in the events table as `charging → DBG` transitions and as
 * anomalies raised on their placeholder zeros. Here they are what they always
 * were: the charger talking to the server about itself. Readings are not shown
 * because these frames carry none — the raw frame, for an admin, is a click
 * away in the drawer.
 */
export function ServiceFramesTable({
  events,
  tz,
  isLoading,
  onSelect,
}: {
  events: TelemetryEvent[];
  tz: TelemetryTz;
  isLoading?: boolean;
  onSelect?: (event: TelemetryEvent) => void;
}) {
  const { t, catalog } = useCatalog();
  const columns = React.useMemo<ColumnDef<TelemetryEvent, any>[]>(
    () => [
      {
        id: "ts",
        header: t("operator.telemetry.column_time", { zone: tz }),
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">{formatTs(row.original.ts, tz)}</span>
        ),
      },
      {
        id: "frame",
        header: t("operator.telemetry.column_frame"),
        cell: ({ row }) => {
          const marker = serviceMarkerOf(row.original);
          const spec = MARKER[marker];
          return (
            <Badge variant={spec?.variant ?? "neutral"}>
              {spec?.value === "rejected" ? t("operator.telemetry.marker_rejected") : marker}
            </Badge>
          );
        },
      },
      {
        id: "note",
        header: t("operator.telemetry.column_what_it_is"),
        cell: ({ row }) => {
          const marker = serviceMarkerOf(row.original);
          const detail = row.original.detail as ServiceDetail | null;
          const note = t("operator.telemetry.marker_note", { marker: MARKER[marker]?.value ?? "other" });
          const state = row.original.state;
          const text =
            detail?.marker === "REJECTED" && state
              ? t("operator.telemetry.marker_note_parsed", { note, state })
              : note;
          return <span className="text-xs text-muted-foreground">{text}</span>;
        },
      },
    ],
    [tz, t, catalog],
  );

  return (
    <DataTableShell height="auto" minHeight="0" className="[&>div]:max-h-[320px]">
      <DataTable
        columns={columns}
        data={events}
        isLoading={isLoading}
        onRowClick={onSelect}
        tableClassName="text-xs"
        emptyMessage={t("operator.telemetry.service_frames_empty")}
      />
    </DataTableShell>
  );
}
